import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsHttpGetAssetsByAssetIdSegmentsQuery,
  type AssetsSegmentElasticSchemaWithIdNullable,
  httpDeleteAssetsByAssetIdSegmentsBySegmentId,
  httpGetAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpDeleteFaceRecognitionAssetsByAssetIdPersonsBulk,
  httpDeleteFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionId,
  httpDeleteFaceRecognitionPersonsByPersonIdBulk,
  httpGetFaceRecognitionAssetsByAssetIdVersionsByVersionIdPersons,
  httpPostFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionIdChangePerson,
  httpPostFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionIdConfirmPerson,
  httpPostFaceRecognitionPersonsByPersonIdConfirmPerson,
  httpPostFaceRecognitionPersonsByPersonIdUpdateAndConfirm,
  httpPutFaceRecognitionPersonsByPersonId,
  type MlBulkConfirmPersonSchema,
  type MlChangePersonInstanceSchema,
  type MlDeletePerson,
  type MlDeletePersonByVersion,
  type MlUpdateConfirmPersonSchema,
  type MlUpdatePersonSchema,
} from '@iconik-io/iconik-api/src/services/ml';
import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import {
  deleteNormalizedEntity,
  updateEntities,
} from '@iconik-io/shared-lib/src/redux/entities/slice';
import type { PlayerStore } from '@iconik-io/unified-player-core';

import type { AppDispatch, GetState } from '../../../store/types';
import { commonListActions } from '../../common_list/slice';
import {
  type CommonListGetParams,
  wrapCommonCreate,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../../common_list/thunks';
import { setActivePerson, setInConfirmationMode } from './slice';

const actions = commonListActions('asset_person_segments');
const adminPersonInstancesActions = commonListActions('admin_person_instances');

type GetPersonsByAssetVersionParam = CommonListGetParams & {
  assetId: string;
  versionId: string;
};

const defaultParams = {
  per_page: 100,
  segment_type: 'PERSON',
  page: 1,
  sort: 'time_start_milliseconds,asc',
};

export function getPersonsByAssetVersion(
  params: GetPersonsByAssetVersionParam,
) {
  return (dispatch: AppDispatch) =>
    wrapCommonGetList({
      prefix: 'asset_persons',
      pagination: 'regular',
      dispatch,
      params,
      getListPromise: (listParams) =>
        dispatch(
          httpGetFaceRecognitionAssetsByAssetIdVersionsByVersionIdPersons(
            listParams?.assetId,
            listParams?.versionId,
          ),
        ),
    });
}

export const segmentList = commonListActions('segments');
export const assetPersonsList = commonListActions('asset_persons');

export function deletePersonInstanceByPersonIdFromAssetVersion(
  personId: string,
  assetId: string,
  versionId: string,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    wrapCommonDelete({
      prefix: 'asset_persons',
      dispatch,
      ids: [personId],
      deletePromise: dispatch(
        httpDeleteFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionId(
          personId,
          assetId,
          versionId,
        ),
      ).then(() => {
        const segmentsIds = Object.values(getState().entities.segments)
          .filter((item) => item.person_id && item.person_id === personId)
          .map((item) => item.id);

        dispatch(segmentList.deleteIds(segmentsIds));
        dispatch(assetPersonsList.deleteIds([personId]));
      }),
    });
}

export const deleteMultiplePersonInstancesByPersonIdsFromAssetVersion =
  (assetId: string, persons: MlDeletePersonByVersion[]) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    wrapCommonDelete({
      prefix: 'asset_persons',
      dispatch,
      ids: [assetId],
      deletePromise: dispatch(
        httpDeleteFaceRecognitionAssetsByAssetIdPersonsBulk(assetId, {
          objects: persons,
        }),
      ).then(() => {
        const segmentsIds = Object.values(getState().entities.segments)
          .filter((item) => item.asset_id === assetId && item.person_id)
          .map((item) => item.id);

        dispatch(segmentList.deleteIds(segmentsIds));
        dispatch(
          assetPersonsList.deleteIds(persons.map((item) => item.person_id)),
        );
      }),
    });

export function deletePersonInstanceByPersonIdFromMultipleAssets(
  personId: string,
  objects: MlDeletePerson[],
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    wrapCommonDelete({
      prefix: 'asset_persons',
      dispatch,
      ids: [personId],
      deletePromise: dispatch(
        httpDeleteFaceRecognitionPersonsByPersonIdBulk(personId, { objects }),
      ).then(() => {
        const segmentsIds = Object.values(getState().entities.segments)
          .filter((item) => item.person_id && item.person_id === personId)
          .map((item) => item.id);

        dispatch(segmentList.deleteIds(segmentsIds));
        dispatch(assetPersonsList.deleteIds([personId]));
      }),
    });
}

export const mapSegments = (
  segments: AssetsSegmentElasticSchemaWithIdNullable[],
  personId: string,
) =>
  // Search answers with the elastic segments, whose fields are nullable, while
  // the normalized entity has them required. Nothing normalizes in between.
  // biome-ignore lint/plugin: see above.
  Object.fromEntries(
    segments.map((item) => [item.id, { ...item, person_id: personId }]),
  ) as {
    [id: string]: Partial<SegmentEntityWithIdNormalized>;
  };

export const replacePersonBasedOnVersion = (
  personId: string,
  assetId: string,
  versionId: string,
  changePersonBody: MlChangePersonInstanceSchema,
  segments?: AssetsSegmentElasticSchemaWithIdNullable[],
  personVersionFaceId?: string | null,
) => {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'asset_persons',
      dispatch,
      createPromise: dispatch(
        httpPostFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionIdChangePerson(
          personId,
          assetId,
          versionId,
          changePersonBody,
        ),
      ).then((result) => {
        if (segments) {
          dispatch(
            deleteNormalizedEntity({ key: 'asset_persons', id: personId }),
          );
          const entities = mapSegments(segments, result.body.id);

          dispatch(
            updateEntities({
              segments: entities,
            }),
          );
        }
        if (personVersionFaceId) {
          dispatch(
            adminPersonInstancesActions.deleteIds([personVersionFaceId]),
          );
        }

        return result;
      }),
    });
};

export const updatePerson = (
  personId: string,
  updatePersonBody: MlUpdatePersonSchema,
  saveAsNewPerson?: boolean,
) => {
  return async (dispatch: AppDispatch) =>
    await wrapCommonCreate({
      prefix: 'asset_persons',
      dispatch,
      createPromise: dispatch(
        httpPutFaceRecognitionPersonsByPersonId(personId, updatePersonBody),
      ).then((response) => {
        const entitiesBody = structuredClone(response.body);

        if (saveAsNewPerson && updatePersonBody.name) {
          entitiesBody.status = 'USER_CONFIRMED';
        }
        dispatch(
          updateEntities({
            asset_persons: {
              [personId]: {
                ...entitiesBody,
              },
            },
          }),
        );

        return response;
      }),
    });
};

export const addPerson = (
  personId: string,
  addPersonBody: MlUpdateConfirmPersonSchema,
  saveAsNewPerson?: boolean,
) => {
  return async (dispatch: AppDispatch) => {
    const response = await wrapCommonCreate({
      prefix: 'asset_persons',
      dispatch,
      createPromise: dispatch(
        httpPostFaceRecognitionPersonsByPersonIdUpdateAndConfirm(
          personId,
          addPersonBody,
        ),
      ),
    });

    const entitiesBody = structuredClone(response.body);
    if (saveAsNewPerson && addPersonBody.name) {
      entitiesBody.status = 'USER_CONFIRMED';
    }

    dispatch(
      updateEntities({
        asset_persons: { [personId]: { ...entitiesBody } },
        persons: {
          [personId]: {
            status: entitiesBody.status,
          },
        },
      }),
    );

    return response;
  };
};

export const confirmAssetVersionPerson = (
  personId: string,
  assetId: string,
  versionId: string,
) => {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'asset_persons',
      dispatch,
      createPromise: dispatch(
        httpPostFaceRecognitionPersonsByPersonIdAssetsByAssetIdVersionsByVersionIdConfirmPerson(
          personId,
          assetId,
          versionId,
        ),
      ).then((response) => {
        dispatch(
          updateEntities({
            asset_persons: {
              [personId]: {
                status: 'USER_CONFIRMED',
                has_unconfirmed_instances: false,
              },
            },
          }),
        );
        return response;
      }),
    });
};

export const confirmPersonBulk = (
  personId: string,
  body: MlBulkConfirmPersonSchema,
) => {
  return (dispatch: AppDispatch) =>
    wrapCommonCreate({
      prefix: 'asset_persons',
      dispatch,
      createPromise: dispatch(
        httpPostFaceRecognitionPersonsByPersonIdConfirmPerson(personId, body),
      ).then((response) => {
        dispatch(
          updateEntities({
            asset_persons: {
              [personId]: {
                status: 'USER_CONFIRMED',
              },
            },
            persons: {
              [personId]: {
                status: 'USER_CONFIRMED',
              },
            },
          }),
        );
        return response;
      }),
    });
};

export const deletePersonSegment =
  (assetId: string, segmentId: string) =>
  (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      httpDeleteAssetsByAssetIdSegmentsBySegmentId(assetId, segmentId),
    ).then(() => {
      dispatch(deleteNormalizedEntity({ key: 'segments', id: segmentId }));
      dispatch(actions.deleteId(segmentId));
    });

// Recursively get all pages from the persons' segments
export const getPersonsSegments =
  (assetId: string, params?: AssetsHttpGetAssetsByAssetIdSegmentsQuery) =>
  (dispatch: AppDispatch) => {
    const requestParams: AssetsHttpGetAssetsByAssetIdSegmentsQuery = {
      ...defaultParams,
      ...params,
    };

    let segments: AssetsSegmentElasticSchemaWithIdNullable[] = [];

    const fetchAllSegments = (
      {
        body,
      }: EndpointResponse<ReturnType<typeof httpGetAssetsByAssetIdSegments>>,
      page: number | undefined,
    ): Promise<AssetsSegmentElasticSchemaWithIdNullable[]> => {
      segments = segments.concat(body.objects);

      if (page && body.pages > page) {
        return dispatch(
          httpGetAssetsByAssetIdSegments(assetId, {
            ...requestParams,
            page: page + 1,
          }),
        ).then((response) => fetchAllSegments(response, page + 1));
      }
      dispatch(actions.setIds(segments.map((item) => item.id)));
      return Promise.resolve(segments);
    };

    return dispatch(
      httpGetAssetsByAssetIdSegments(assetId, requestParams),
    ).then((response) => fetchAllSegments(response, requestParams.page));
  };

export type SetActivePersonSegmentOptions = {
  shouldPlay?: boolean;
};

/** Jump to person *segmentId* in player *store*, trigger confirmation mode depending on person status. */
export const setActivePersonSegment =
  (
    store: PlayerStore,
    segmentId: string,
    isFullscreen: boolean,
    { shouldPlay = false }: SetActivePersonSegmentOptions = {},
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const segment = state.entities.segments[segmentId];
    const personId = segment?.person_id;
    if (!personId || !segment) {
      return;
    }

    // Trigger confirmation mode if the person is unconfirmed/new
    const person = state.entities.asset_persons[personId];
    if (!isFullscreen) {
      const isUnconfirmed =
        person?.status === 'UNCONFIRMED' || person?.status === 'NEW';
      const canStartConfirmationMode = Boolean(
        selectUserRoles(state).can_add_person,
      );
      dispatch(
        setInConfirmationMode(isUnconfirmed && canStartConfirmationMode),
      );
    }

    // Select the person and segment in the player
    store.segment.setSelectedSegmentIds([segment.id]);
    dispatch(setActivePerson({ id: personId }));

    // Start playback if requested
    if (shouldPlay) {
      store.playableMedia.play();
    }
  };
